import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MarkdownWithToc from '../../components/common/MarkdownWithToc';
import NotFound from '../errors/NotFound';
import { formatDate } from '../../components/blog/PostCard';
import { loadPostBody } from '../../common/content';
import { useLocalizedBody } from '../../common/useLocalizedBody';
import { PRIMARY_COLORS } from '../../common/colors';
import posts from '../../data/posts.json';
import PageMeta, { SITE } from '../../components/common/PageMeta';
import JsonLd from '../../components/common/JsonLd';
import GAService from '../../services/GAService';
import { Byline, AboutAuthors } from '../../components/blog/Byline';
import { oclProfileURL } from '../../common/constants';

const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 100];

// Webflow detail_blog-posts.html: centered title header with author/date/
// read-time meta and hero image, then content27 (TOC + body).
const BlogPost = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const post = posts.find(p => p.slug === slug);
  const { body, fallback } = useLocalizedBody(post, loadPostBody);

  React.useEffect(() => {
    if (post)
      GAService.recordBlogRead(post);
  }, [post?.slug]);

  // Depth waits for the body: before it renders the page is header-only, so
  // any scroll reads as 100%. The first check runs a frame after mount (after
  // ScrollToTop has reset the position) so posts too short to scroll still
  // report. firedDepths is a ref so a language switch (new body, same post)
  // doesn't resend depths already reached.
  const firedDepths = React.useRef(new Set());
  React.useEffect(() => {
    firedDepths.current = new Set();
  }, [post?.slug]);

  React.useEffect(() => {
    if (!post || !body)
      return undefined;

    const checkDepth = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percentScrolled = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
      SCROLL_DEPTH_THRESHOLDS.forEach(depth => {
        if (percentScrolled >= depth && !firedDepths.current.has(depth)) {
          firedDepths.current.add(depth);
          GAService.recordBlogScrollDepth(post, depth);
        }
      });
    };
    const frame = window.requestAnimationFrame(checkDepth);
    window.addEventListener('scroll', checkDepth, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', checkDepth);
    };
  }, [post?.slug, body]);

  if (!post)
    return <NotFound />;

  const postUrl = `${SITE}/blog/${post.slug}`;
  // docs/SEO-AUDIT.md §6.1 — drives the date/author/thumbnail treatment in
  // Google's article rich results.
  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt || undefined,
    datePublished: post.date,
    dateModified: post.date,
    author: post.authors.length
      ? post.authors.map(author => ({
        '@type': 'Person',
        name: author.name,
        jobTitle: author.title,
        ...(author.ocl ? { url: oclProfileURL(author.ocl) } : {}),
      }))
      : { '@id': `${SITE}/#org` },
    publisher: { '@id': `${SITE}/#org` },
    image: post.image ? `${SITE}${post.image}` : undefined,
    mainEntityOfPage: postUrl,
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('nav.blog'), item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 2, name: post.title, item: postUrl },
    ],
  };

  return (
    <Container component="article" maxWidth="lg" sx={{ py: { xs: 2, md: 2 } }}>
      {/* ogImage, not image: the card thumbnail can still be a portrait or a
          small image (in-body author headshots were removed — the byline
          and About box carry those now), which makes a poor share card.
          build-blog-index.js only sets ogImage when the file is actually
          shaped like one; otherwise this falls through to PageMeta's
          default card. */}
      <PageMeta title={post.title} description={post.excerpt || undefined} image={post.ogImage || undefined} type="article" />
      <JsonLd data={blogPostingJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <Button
        component={RouterLink}
        to="/blog"
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '12px !important' }} />}
        sx={{ color: 'primary.main', mb: 3, px: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
        {t('blog.back_button')}
      </Button>
      <Box sx={{ mb: { xs: 0, md: 0 } }}>
        {post.category && (
          <Chip label={post.category} size="small" sx={{ backgroundColor: PRIMARY_COLORS['95'], color: 'primary.main', fontWeight: 600, mb: 2 }} />
        )}
        <Typography variant="h2" component="h1" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
          {post.title}
        </Typography>
        <Byline
          authors={post.authors}
          date={formatDate(post.date, i18n.language)}
          readTime={t('common.min_read', { count: post.readMinutes })} />
      </Box>
      {post.heroImage && (
        <Box
          component="img"
          src={post.heroImage}
          alt=""
          sx={{ width: '100%', maxHeight: 480, objectFit: 'cover', borderRadius: '12px', mb: { xs: 4, md: 6 } }}
        />
      )}
      {fallback && <Alert severity="info" sx={{ mb: 3 }}>{t('content.not_translated')}</Alert>}
      {/* docs/SEO-AUDIT.md §7.2 — post bodies start at markdown ### (H3),
          skipping H2; a visually-hidden heading here avoids editing all
          39 posts' markdown to fix the sequence. */}
      <Typography component="h2" sx={visuallyHidden}>{t('blog.post_content_heading')}</Typography>
      <MarkdownWithToc body={body} />
      <AboutAuthors authors={post.authors} />
    </Container>
  );
};

export default BlogPost;
